import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Box, Card, CardContent, Stack, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { cardTitleSx, monoFont, numberSx, tokens } from "../../theme";

const CHART_HEIGHT = 190;
const PAD = { left: 58, right: 14, top: 14, bottom: 30 };

const labelSx = { fontSize: "11.5px", color: tokens.muted };

/**
 * Least-squares slope and intercept of v against x, plus the RMS scatter about that line.
 *
 * The slope is the quantity that actually reaches a shim: shims come from the slope of a best-fit
 * line through the circle centres, so a clip changes a shim exactly to the extent that it tilts
 * that line. How far the shift scatters, or how large its average value is, changes nothing on its
 * own - a 100 µm shift applied equally to every circle moves no shim at all.
 */
const fitLine = (points, key) => {
    const n = points.length;
    if (n < 2) return null;

    let sx = 0, sv = 0;
    for (const p of points) { sx += p.x; sv += p[key]; }
    const mx = sx / n;
    const mv = sv / n;

    let sxv = 0, sxx = 0;
    for (const p of points) {
        const dx = p.x - mx;
        sxv += dx * (p[key] - mv);
        sxx += dx * dx;
    }
    if (sxx === 0) return null;

    const slope = sxv / sxx;
    const intercept = mv - slope * mx;

    let sse = 0;
    for (const p of points) {
        const residual = p[key] - (slope * p.x + intercept);
        sse += residual * residual;
    }

    return { slope, intercept, rms: Math.sqrt(sse / n) };
};

/** Robust percentile from an already-sorted array. */
const at = (sorted, fraction) =>
    sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(fraction * (sorted.length - 1))))];

/**
 * How far angular clipping moved each circle's fitted centre, plotted against shaft position.
 *
 * This exists because the obvious presentation - drawing the clipped and unclipped centres on top
 * of each other on the Y/Z charts - cannot work. Those axes span 1000+ µm of real shaft shape and
 * the shift is single-digit µm, so the two series overlap exactly and read as "clipping changed
 * nothing", which is the opposite of the truth. Plotting the DIFFERENCE gives it an axis scaled to
 * its own size, where a few µm is the whole plot.
 *
 * What the shape means, and it is the entire question:
 *   - A FLAT line at some non-zero value: clipping moved every centre by the same amount. Shims
 *     come from the SLOPE of a best-fit line through the centres, and a constant offset does not
 *     change a slope - so a clip like this cannot change a shim, whatever its magnitude.
 *   - A line that WANDERS along x: the shift is position-dependent, which does change the slope,
 *     and therefore can change the shim in either direction.
 * Magnitude alone says nothing; only the variation does.
 */
export default function ClipShiftChart() {
    const rawSurface = useSelector((state) => state.rawSurface);
    const canvasRef = useRef(null);
    const deltas = rawSurface.clipDeltas;

    const stats = useMemo(() => {
        if (!deltas?.length) return null;
        const zs = deltas.map((d) => d.deltaZ).sort((a, b) => a - b);
        const ys = deltas.map((d) => d.deltaY).sort((a, b) => a - b);
        const inRange = (range) => (range
            ? deltas.filter((d) => d.x >= Math.min(range.start, range.stop) && d.x <= Math.max(range.start, range.stop))
            : []);

        return {
            medianZ: at(zs, 0.5),
            spreadZ: at(zs, 0.95) - at(zs, 0.05),
            medianY: at(ys, 0.5),
            spreadY: at(ys, 0.95) - at(ys, 0.05),
            fitAll: fitLine(deltas, "deltaZ"),
            // Per shaft, because that is how the shim is computed - one line through the machine
            // centres and one through the motor centres. A tilt confined to one shaft is exactly
            // what moves a shim; a tilt shared by both partly cancels.
            fitMachine: fitLine(inRange(rawSurface.machineRange), "deltaZ"),
            fitMotor: fitLine(inRange(rawSurface.motorRange), "deltaZ"),
        };
    }, [deltas, rawSurface.machineRange, rawSurface.motorRange]);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !deltas?.length || !stats) return;

        const ratio = window.devicePixelRatio || 1;
        const width = canvas.clientWidth;
        const height = CHART_HEIGHT;
        canvas.width = width * ratio;
        canvas.height = height * ratio;

        const ctx = canvas.getContext("2d");
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.clearRect(0, 0, width, height);

        const plotW = width - PAD.left - PAD.right;
        const plotH = height - PAD.top - PAD.bottom;

        let xMin = Infinity;
        let xMax = -Infinity;
        for (const d of deltas) {
            if (d.x < xMin) xMin = d.x;
            if (d.x > xMax) xMax = d.x;
        }
        if (xMax === xMin) xMax = xMin + 1;

        // Scaled to the 2nd-98th percentile of the shifts, not min/max: a handful of circles where
        // clipping happened to break the fit would otherwise flatten everything else into a line.
        // Zero is always included, so "did this move at all" stays readable.
        const all = deltas.flatMap((d) => [d.deltaY, d.deltaZ]).sort((a, b) => a - b);
        let lo = Math.min(0, at(all, 0.02));
        let hi = Math.max(0, at(all, 0.98));
        const pad = Math.max((hi - lo) * 0.15, 0.5);
        lo -= pad;
        hi += pad;

        const sx = (x) => PAD.left + ((x - xMin) / (xMax - xMin)) * plotW;
        const sy = (v) => PAD.top + plotH - ((v - lo) / (hi - lo)) * plotH;

        ctx.fillStyle = tokens.paper;
        ctx.fillRect(PAD.left, PAD.top, plotW, plotH);

        // Horizontal grid, with zero emphasised - the line that means "clipping did nothing here".
        const step = niceStep(hi - lo);
        ctx.font = `10px ${monoFont}`;
        ctx.textAlign = "right";
        for (let v = Math.ceil(lo / step) * step; v <= hi; v += step) {
            const y = Math.round(sy(v)) + 0.5;
            ctx.strokeStyle = Math.abs(v) < step / 100 ? tokens.muted2 : tokens.line;
            ctx.beginPath();
            ctx.moveTo(PAD.left, y);
            ctx.lineTo(PAD.left + plotW, y);
            ctx.stroke();
            ctx.fillStyle = tokens.muted2;
            ctx.fillText(v.toFixed(step < 1 ? 1 : 0), PAD.left - 7, y + 3.5);
        }

        // Median shift, dashed: if the cloud hugs this line the shift is a constant offset.
        const medianLine = (value, color) => {
            const y = Math.round(sy(value)) + 0.5;
            ctx.save();
            ctx.setLineDash([5, 4]);
            ctx.strokeStyle = color;
            ctx.lineWidth = 1;
            ctx.globalAlpha = 0.8;
            ctx.beginPath();
            ctx.moveTo(PAD.left, y);
            ctx.lineTo(PAD.left + plotW, y);
            ctx.stroke();
            ctx.restore();
        };

        const plot = (key, color) => {
            ctx.fillStyle = color;
            for (const d of deltas) {
                const y = sy(d[key]);
                if (y < PAD.top || y > PAD.top + plotH) continue;
                ctx.fillRect(sx(d.x) - 0.75, y - 0.75, 1.5, 1.5);
            }
        };

        plot("deltaY", tokens.reject);
        plot("deltaZ", tokens.kept);
        medianLine(stats.medianZ, tokens.kept);

        // The trend through the shift. Its tilt, not the cloud's thickness, is what moves a shim -
        // so this line is the one thing on the chart worth reading closely.
        if (stats.fitAll) {
            ctx.strokeStyle = tokens.text;
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(sx(xMin), sy(stats.fitAll.slope * xMin + stats.fitAll.intercept));
            ctx.lineTo(sx(xMax), sy(stats.fitAll.slope * xMax + stats.fitAll.intercept));
            ctx.stroke();
        }

        ctx.fillStyle = tokens.muted;
        ctx.textAlign = "center";
        ctx.fillText("shaft position (µm)", PAD.left + plotW / 2, height - 3);
        ctx.textAlign = "left";
        ctx.fillText(Math.round(xMin).toLocaleString(), PAD.left, PAD.top + plotH + 14);
        ctx.textAlign = "right";
        ctx.fillText(Math.round(xMax).toLocaleString(), PAD.left + plotW, PAD.top + plotH + 14);

        ctx.save();
        ctx.translate(12, PAD.top + plotH / 2);
        ctx.rotate(-Math.PI / 2);
        ctx.textAlign = "center";
        ctx.fillText("centre shift (µm)", 0, 0);
        ctx.restore();
    }, [deltas, stats]);

    useEffect(() => { draw(); }, [draw]);
    useEffect(() => {
        const onResize = () => draw();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [draw]);

    if (!deltas?.length || !stats) return null;

    // Total tilt the clip introduced, end to end, in microns - slope expressed over the span it
    // was measured on. This is the shim-relevant quantity: a shift that ends where it started
    // tilts nothing, however much it wandered in between.
    const span = deltas.length ? deltas[deltas.length - 1].x - deltas[0].x : 0;
    const tiltZ = stats.fitAll ? stats.fitAll.slope * span : 0;

    // A tilt smaller than the scatter about it is not a trend, it is the residue of fitting a line
    // to noise - saying otherwise would read structure into a flat cloud.
    const meaningfulTilt = stats.fitAll ? Math.abs(tiltZ) > stats.fitAll.rms : false;

    const perShaft = (fit, label) => {
        if (!fit) return null;
        return `${label} ${(fit.slope * 1e5).toFixed(2)} µm/100 mm`;
    };

    return (
        <Card sx={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.line}` }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: "8px" }}>
                    <Typography sx={cardTitleSx}>Clip Shift</Typography>
                    <Tooltip
                        title={
                            <>
                                How far angular clipping moved each circle&rsquo;s fitted centre —
                                clipped minus unclipped, with identical circle-fixing settings on
                                both sides.
                                <br /><br />
                                A flat line means every centre moved by the same amount. Shims come
                                from the slope of a line through these centres, so a constant offset
                                cannot change a shim no matter how large it is. Only a shift that
                                varies along the shaft can.
                            </>
                        }
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 15, color: tokens.muted2 }} />
                    </Tooltip>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: meaningfulTilt ? tokens.warn : tokens.kept }}>
                        {meaningfulTilt
                            ? `Tilts the centre line ${tiltZ.toFixed(1)} µm end to end — changes the shim`
                            : "No real tilt — the shift is offset plus scatter, which cancels"}
                    </Typography>
                </Stack>

                <canvas ref={canvasRef} style={{ width: "100%", height: CHART_HEIGHT, display: "block" }} />

                <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={0.5} sx={{ mt: "8px" }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 7, height: 7, backgroundColor: tokens.kept }} />
                        <Typography sx={labelSx}>
                            ΔZ median <span style={numberSx}>{stats.medianZ.toFixed(2)}</span> µm,
                            {" spread "}<span style={numberSx}>{stats.spreadZ.toFixed(2)}</span> µm
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 7, height: 7, backgroundColor: tokens.reject }} />
                        <Typography sx={labelSx}>
                            ΔY median <span style={numberSx}>{stats.medianY.toFixed(2)}</span> µm,
                            {" spread "}<span style={numberSx}>{stats.spreadY.toFixed(2)}</span> µm
                        </Typography>
                    </Stack>
                    <Box sx={{ flexGrow: 1 }} />
                    <Typography sx={labelSx}>
                        <span style={numberSx}>{deltas.length.toLocaleString()}</span> circles compared
                    </Typography>
                </Stack>

                <Stack direction="row" flexWrap="wrap" alignItems="center" columnGap={2} rowGap={0.5} sx={{ mt: "6px" }}>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Box sx={{ width: 16, height: 0, borderTop: `2px solid ${tokens.text}` }} />
                        <Typography sx={labelSx}>
                            ΔZ trend{" "}
                            <span style={numberSx}>{stats.fitAll ? (stats.fitAll.slope * 1e5).toFixed(2) : "—"}</span>
                            {" µm/100 mm, scatter about it "}
                            <span style={numberSx}>{stats.fitAll ? stats.fitAll.rms.toFixed(2) : "—"}</span> µm
                        </Typography>
                    </Stack>
                    {(stats.fitMachine || stats.fitMotor) && (
                        <Typography sx={labelSx}>
                            per shaft:{" "}
                            <span style={numberSx}>
                                {[perShaft(stats.fitMachine, "machine"), perShaft(stats.fitMotor, "motor")]
                                    .filter(Boolean).join(" · ")}
                            </span>
                        </Typography>
                    )}
                    {!stats.fitMachine && !stats.fitMotor && (
                        <Typography sx={labelSx}>
                            Set machine/motor ranges to see the per-shaft tilt — that is what the shim uses.
                        </Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

/** 1/2/5 x 10^n step giving roughly 4-6 labelled gridlines. */
function niceStep(range) {
    const rough = range / 5;
    const power = Math.pow(10, Math.floor(Math.log10(rough)));
    const scaled = rough / power;
    return (scaled >= 5 ? 5 : scaled >= 2 ? 2 : 1) * power;
}
